import { useMutation } from '@tanstack/react-query';
import { useShowErrorToast } from '@utils/formHelpers';
import { queryFetch } from '@services/api';
import { CreateTaskReq } from './types';
import { useJobRefetch } from '@hooks/job/refetch';
import { DefaultApiResp } from '@root/globalTypes';
import { defaultAppearance } from '@forms/panel/workflows/task/constants';

export const useCreateTask = () => {
  const { refetch, stages } = useJobRefetch();
  const { showErrorToast } = useShowErrorToast();

  const { mutate, isPending } = useMutation({
    mutationFn: ({ jobId, ...task }: CreateTaskReq): Promise<DefaultApiResp> =>
      queryFetch({
        url: `jobs/${jobId}/tasks`,
        data: { task },
        opt: { method: 'POST' },
      }),
    onMutate: async ({ jobId, taskName: name, stageId }) =>
      stages.update({
        jobId,
        stage: {
          id: stageId,
          tasks: stages
            .get(jobId)
            ?.find((el) => el.id === stageId)
            ?.tasks.map((el) => ({
              ...el,
              tasks: [...el.tasks, { id: name, name, appearance: defaultAppearance }],
            })),
        },
      }),
    onError: (err) => {
      console.error(err);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      await refetch();
    },
    onSettled: (data, _, { jobId }, prev) => {
      if (!data) return;
      const { errors } = data as DefaultApiResp;
      if (errors) {
        stages.restore(jobId, prev);
        showErrorToast(errors);
      }
    },
  });

  return {
    mutate,
    isLoading: isPending,
  };
};
